import React, { useState, useEffect } from 'react';

import useDataSourceManager from "hooks/useDataSourceManager";

import { v4 as uuidv4 } from "uuid";

import { useTheme, styled } from "@mui/material/styles";
import { Box, CircularProgress, Typography } from "@mui/material";

import InfoCardField from "components/cards/InfoCardField"
import LineChart from "components/LineChart";
import { MenuSelect } from "components/Select/menuSelect";

import { ReactComponent as DisplayClockIcon } from "assets/images/icons/icn_display_clock.svg";

import { getLineChartData } from "utils/getEChartData";

// Telemetry timestamps are the start of a fifteen-minute aggregate bucket.
const FIFTEEN_MINUTE_BUCKET_MS = 15 * 60 * 1000;

const LoadingContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  gap: 2,
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  margin: 2,
}));

const CenteredErrorBox = styled(Box)(() => ({
  display: "flex",
  justifyContent: "center",
  width: "100%",
  textAlign: "center",
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.redPrimary,
  fontSize: "15px",
  whiteSpace: "normal",
  wordBreak: "break-word",
  margin: "20px",
}));

const NoDataText = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.greyPrimary,
  fontSize: "15px",
  margin: "20px",
}));

const StatisticsContent = ({ auth, infoData }) => {
  const theme = useTheme();

  const [statisticsUuid, _setStatisticsUuid] = useState(uuidv4());
  const [cpuMemoryData, setCpuMemoryData] = useState([]);
  const [cpuMemoryErrorData, setCpuMemoryErrorData] = useState("");

  const [trafficData, setTrafficData] = useState([]);
  const [trafficErrorData, setTrafficErrorData] = useState("");

  const [wanList, setWanList] = useState([]);
  const [selectedWan, setSelectedWan] = useState("");

  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const { dataMap, getData, sendRequest } = useDataSourceManager();

  useEffect(() => {
    const standalone = !auth.orgs.id || Object.keys(auth.orgs).length === 0;
    const apiStatisticsUrl = `${standalone ? "" : "/ents/:entId"}/orgs/:orgId/sites/:siteId/devices/:deviceId/stats.read`;
    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm.id,
        siteId: auth.scopeLevel === 'org' ? infoData.siteId : auth.sites.id,
        deviceId: infoData.macAddress
      },
      nonStandalone: {
        entId: auth.realm.id,
        orgId: auth.orgs.id,
        siteId: auth.scopeLevel === 'org' ? infoData.siteId : auth.sites.id,
        deviceId: infoData.macAddress
      },
    };

    const requestParams = {
      "data": {
        "cpuStats": {
          "range": "1d"
        },
        "memStats": {
          "range": "1d"
        },
        ...(infoData.deviceType === "router" ?
          {
            "wanTrafficStats": {
              "range": "1d"
            }
          } : {
            "trafficStats": {
              "range": "1d"
            },
          }
        )
      }
    };

    const mode = standalone ? "standalone" : "nonStandalone";
    const restfulParams = restfulParamsConfig[mode];
    setStatisticsLoading(true);
    const fetchStatistics = () => sendRequest(apiStatisticsUrl, statisticsUuid, restfulParams, requestParams);
    fetchStatistics()

    const intervalId = setInterval(fetchStatistics, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [infoData.macAddress])

  useEffect(() => {
    const response = getData(statisticsUuid);
    if (response && response.data) {
      const data = response.data
      const { cpuStats, memStats, trafficStats, wanTrafficStats } = data;

      const cpuMemoryGroup = { cpuStats, memStats };

      const cpuMemoryLineChartData = getLineChartData({ cpuStats: "CPU", memStats: "Memory" }, cpuMemoryGroup);
      setCpuMemoryData(cpuMemoryLineChartData);

      if (infoData.deviceType === "router" && wanTrafficStats) {
        setWanList(wanTrafficStats);

        const defaultWan = wanTrafficStats[0];
        if (!selectedWan) {
          setSelectedWan(defaultWan.interfaceName);
        }

        const currentWan = wanTrafficStats.find(item => item.interfaceName === selectedWan) || defaultWan;
        const trafficGroup = currentWan.trafficStats;
        const trafficLineChartData = getLineChartData({ txStats: "Tx", rxStats: "Rx" }, trafficGroup, "MB");

        setTrafficData(trafficLineChartData);
      } else {
        const trafficGroup = { ...trafficStats };
        const trafficLineChartData = getLineChartData({ txStats: "Tx", rxStats: "Rx" }, trafficGroup, "KB");
        setTrafficData(trafficLineChartData);
      }

      setCpuMemoryErrorData("");
      setTrafficErrorData("");
      setStatisticsLoading(false);
    }
    if (response && response.code) {
      if (response.code === 400 && response.message?.includes("No aggregated data found")) {
        setCpuMemoryErrorData("No data available")
        setTrafficErrorData("No data available")
      } else {
        const data = response.message
        setCpuMemoryErrorData(data)
        setTrafficErrorData(data)
      }
      setStatisticsLoading(false);
    }
  }, [dataMap]);

  useEffect(() => {
    if (!wanList.length || !selectedWan) return;

    const currentWan = wanList.find(w => w.interfaceName === selectedWan);
    if (!currentWan) return;

    const trafficLineChartData = getLineChartData(
      { txStats: "Tx", rxStats: "Rx" },
      currentWan.trafficStats,
      "KB"
    );

    setTrafficData(trafficLineChartData);
  }, [selectedWan, wanList]);

  const wanOptions = wanList.map((wan) => ({
    label: wan.interfaceName,
    value: wan.interfaceName,
  }));

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: 2,
        boxSizing: "border-box",
        gap: "20px",
      }}>
      <Box width="100%" padding="16px">
        <InfoCardField
          mode="stack"
          width={560}
          title="CPU/Memory Monitor"
          metaInfo={{ icon: <DisplayClockIcon />, info: "Last 24 Hours" }}
          infoData={(
            statisticsLoading === true ?
              <LoadingContainer>
                <CircularProgress />
              </LoadingContainer> :
              cpuMemoryErrorData ?
                <CenteredErrorBox>
                  {cpuMemoryErrorData === "No data available" ?
                    <NoDataText>{cpuMemoryErrorData}</NoDataText> :
                    <ErrorText>{cpuMemoryErrorData}</ErrorText>
                  }
                </CenteredErrorBox> :
                <LineChart
                  legendStyle={{ top: 18, right: 10 }}
                  lineChartData={cpuMemoryData}
                  unitData={{ yAxisName: "Usage (%)", tooltipUnit: "%", timeRangeUnit: FIFTEEN_MINUTE_BUCKET_MS, timeRangePosition: "start" }}
                  height="400px"
                />
          )}
        />
      </Box>
      {
        infoData.deviceType === "router" ?
          <Box width="100%" padding="16px">
            <InfoCardField
              mode="stack"
              width={560}
              title="Internet Usage"
              metaInfo={{ icon: <DisplayClockIcon />, info: "Last 24 Hours" }}
              option={
                <Box sx={{ display: "flex", width: "270px", alignItems: "center", justifyContent: "space-evenly" }}>
                  <Typography
                    sx={{
                      color: theme.palette.primary.main,
                      fontSize: theme.typography.customFontSize[2],
                    }}
                  >
                    Interface Type:
                  </Typography>
                  <MenuSelect
                    value={selectedWan}
                    onChange={(opt) => setSelectedWan(opt.value)}
                    options={wanOptions}
                    width={120}
                    placeholder="Select"
                  />
                </Box>
              }
              infoData={(
                statisticsLoading === true ?
                  <LoadingContainer>
                    <CircularProgress />
                  </LoadingContainer> :
                  trafficErrorData ?
                    <CenteredErrorBox>
                      {trafficErrorData === "No data available" ?
                        <NoDataText>{trafficErrorData}</NoDataText> :
                        <ErrorText>{trafficErrorData}</ErrorText>
                      }
                    </CenteredErrorBox> :
                    <LineChart
                      legendStyle={{ top: 18, right: 10 }}
                      lineChartData={trafficData}
                      unitData={{ yAxisName: "Rate (MB)", tooltipUnit: "Kps", timeRangeUnit: FIFTEEN_MINUTE_BUCKET_MS, timeRangePosition: "start" }}
                      height="400px"
                    />
              )}
            />
          </Box> :
          <Box width="100%" padding="16px">
            <InfoCardField
              mode="stack"
              width={560}
              title="Traffic"
              infoData={(
                statisticsLoading === true ?
                  <LoadingContainer>
                    <CircularProgress />
                  </LoadingContainer> :
                  trafficErrorData ?
                    <CenteredErrorBox>
                      {trafficErrorData === "No data available" ?
                        <NoDataText>{trafficErrorData}</NoDataText> :
                        <ErrorText>{trafficErrorData}</ErrorText>
                      }
                    </CenteredErrorBox> :
                    <LineChart
                      legendStyle={{ top: 18, right: 10 }}
                      lineChartData={trafficData}
                      unitData={{ yAxisName: "Rate (Kps)", tooltipUnit: "Kps", timeRangeUnit: FIFTEEN_MINUTE_BUCKET_MS, timeRangePosition: "start" }}
                      height="400px"
                    />
              )}
            />
          </Box>
      }
    </Box>
  )
};

export default StatisticsContent;